import {
  boostOptions,
  estateOptions,
  itemOptions,
  locationOptions,
  skinOptions,
  toolOptions,
  type PickerOption,
} from '../../catalog'
import type { CatalogIndex } from '../../catalog/types'
import {
  LEVEL_PREREQ_TYPES,
  PREREQ_TYPES,
  SPECIAL_REWARD_TYPES,
  type ItemRef,
  type Prereq,
  type SpecialReward,
  type BoostRequirement,
} from '../../domain/types'
import { Combobox } from '../common/Combobox'
import { QualitySelect, RowShell, Stepper } from '../common/fields'

/** item + amount + quality — the shape shared by requirements, toShow and rewards. */
export function ItemRefRow({
  value,
  options,
  onChange,
  onRemove,
}: {
  value: ItemRef
  options: PickerOption[]
  onChange: (next: ItemRef) => void
  onRemove: () => void
}) {
  return (
    <RowShell onRemove={onRemove}>
      <div className="min-w-[16rem] flex-1">
        <Combobox
          ariaLabel="Item"
          value={value.item}
          options={options}
          onChange={(item) => onChange({ ...value, item })}
          placeholder="Search items…"
        />
      </div>
      <Stepper
        ariaLabel="Amount"
        value={value.amount}
        onChange={(amount) => onChange({ ...value, amount })}
      />
      <QualitySelect value={value.quality} onChange={(quality) => onChange({ ...value, quality })} />
    </RowShell>
  )
}

export function PrereqRow({
  value,
  catalog,
  questChoices,
  onChange,
  onRemove,
}: {
  value: Prereq
  catalog: CatalogIndex
  questChoices: PickerOption[]
  onChange: (next: Prereq) => void
  onRemove: () => void
}) {
  const isLevel = (LEVEL_PREREQ_TYPES as readonly string[]).includes(value.type)

  function changeType(type: string) {
    if (type === 'QuestCompleted') onChange({ type: 'QuestCompleted', quest: '' })
    else if (type === 'LocationVisit') onChange({ type: 'LocationVisit', location: '', value: 1 })
    else onChange({ type: type as (typeof LEVEL_PREREQ_TYPES)[number], value: 1 })
  }

  return (
    <RowShell onRemove={onRemove}>
      <select
        aria-label="Prereq type"
        className="rounded border border-edge bg-ink-lift px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
        value={value.type}
        onChange={(event) => changeType(event.target.value)}
      >
        {PREREQ_TYPES.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      {value.type === 'QuestCompleted' && (
        <div className="min-w-[16rem] flex-1">
          <Combobox
            ariaLabel="Prerequisite quest"
            value={value.quest}
            options={questChoices}
            onChange={(quest) => onChange({ type: 'QuestCompleted', quest })}
            placeholder="Search quests…"
          />
        </div>
      )}

      {value.type === 'LocationVisit' && (
        <>
          <div className="min-w-[14rem] flex-1">
            <Combobox
              ariaLabel="Location"
              value={value.location}
              options={locationOptions(catalog)}
              onChange={(location) => onChange({ ...value, location })}
              placeholder="Search locations…"
            />
          </div>
          <Stepper
            ariaLabel="Visits"
            value={value.value}
            onChange={(next) => onChange({ ...value, value: next })}
          />
        </>
      )}

      {isLevel && (
        <Stepper
          ariaLabel="Level"
          min={0}
          value={(value as { value: number }).value}
          onChange={(next) => onChange({ ...(value as Prereq), value: next } as Prereq)}
        />
      )}
    </RowShell>
  )
}

export function SpecialRewardRow({
  value,
  catalog,
  onChange,
  onRemove,
}: {
  value: SpecialReward
  catalog: CatalogIndex
  onChange: (next: SpecialReward) => void
  onRemove: () => void
}) {
  function changeType(type: string) {
    if (type === 'Tool') onChange({ type: 'Tool', tool: '' })
    else if (type === 'Recipe') onChange({ type: 'Recipe', recipe: '' })
    else if (type === 'Skin') onChange({ type: 'Skin', skin: '' })
    else onChange({ type: 'Boost', boost: '', amount: 1 })
  }

  return (
    <RowShell onRemove={onRemove}>
      <select
        aria-label="Special reward type"
        className="rounded border border-edge bg-ink-lift px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
        value={value.type}
        onChange={(event) => changeType(event.target.value)}
      >
        {SPECIAL_REWARD_TYPES.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>

      <div className="min-w-[16rem] flex-1">
        {value.type === 'Tool' && (
          <Combobox
            ariaLabel="Tool"
            value={value.tool}
            options={toolOptions(catalog)}
            onChange={(tool) => onChange({ type: 'Tool', tool })}
            placeholder="Search tools…"
          />
        )}
        {value.type === 'Recipe' && (
          <Combobox
            ariaLabel="Recipe"
            /* Craftables only — a Recipe reward pointing at a resource is rejected on import. */
            value={value.recipe}
            options={itemOptions(catalog, 'craftable')}
            onChange={(recipe) => onChange({ type: 'Recipe', recipe })}
            placeholder="Search craftables…"
          />
        )}
        {value.type === 'Skin' && (
          <Combobox
            ariaLabel="Skin"
            value={value.skin}
            options={skinOptions(catalog)}
            onChange={(skin) => onChange({ type: 'Skin', skin })}
            placeholder="Search skins…"
          />
        )}
        {value.type === 'Boost' && (
          <Combobox
            ariaLabel="Boost"
            value={value.boost}
            options={boostOptions(catalog)}
            onChange={(boost) => onChange({ ...value, boost })}
            placeholder="Search boosts…"
          />
        )}
      </div>

      {value.type === 'Boost' && (
        <Stepper
          ariaLabel="Boost amount"
          value={value.amount}
          onChange={(amount) => onChange({ ...value, amount })}
        />
      )}
    </RowShell>
  )
}

export function BoostRequirementRow({
  value,
  catalog,
  onChange,
  onRemove,
}: {
  value: BoostRequirement
  catalog: CatalogIndex
  onChange: (next: BoostRequirement) => void
  onRemove: () => void
}) {
  return (
    <RowShell onRemove={onRemove}>
      <div className="min-w-[16rem] flex-1">
        <Combobox
          ariaLabel="Boost"
          value={value.boost}
          options={boostOptions(catalog)}
          onChange={(boost) => onChange({ ...value, boost })}
          placeholder="Search boosts…"
        />
      </div>
      <Stepper
        ariaLabel="Uses"
        value={value.amount}
        onChange={(amount) => onChange({ ...value, amount })}
      />
    </RowShell>
  )
}

export function EstateRow({
  value,
  catalog,
  onChange,
  onRemove,
}: {
  value: string
  catalog: CatalogIndex
  onChange: (next: string) => void
  onRemove: () => void
}) {
  return (
    <RowShell onRemove={onRemove}>
      <div className="min-w-[16rem] flex-1">
        <Combobox
          ariaLabel="Estate"
          value={value}
          options={estateOptions(catalog)}
          onChange={onChange}
          placeholder="Search estates…"
        />
      </div>
    </RowShell>
  )
}
